import Experience from "@folio-2026/experience"
import { LoadResult } from "@folio-2026/resources"
import { EVENTS, scaleValue } from "@folio-2026/shared"
import AnimatedModel from "@folio-2026/shared/animatedmodel"
import assert from "assert"
import { SkeletonUtils } from "three/examples/jsm/Addons.js"
import {
  add,
  cameraPosition,
  div,
  dot,
  Fn,
  max,
  mul,
  normalize,
  normalWorld,
  positionWorld,
  pow,
  refract,
  texture,
  uniform,
  vec3,
} from "three/tsl"
import * as THREE from "three/webgpu"
import { Object3D } from "three/webgpu"
import { FolderApi } from "tweakpane"

import OuterParticles from "./OuterParticles"

export default class Man {
  experience: Experience
  debugFolder?: FolderApi

  scene: THREE.Object3D
  resources: {
    [key: string]: LoadResult
  }
  animatedModel!: AnimatedModel
  model!: Object3D<THREE.Object3DEventMap>
  envMap!: THREE.Texture
  perlin!: THREE.Texture
  fbm!: THREE.Texture
  outerParticles!: OuterParticles

  undiDuration: number = 0

  animationCompleted: boolean = false

  settings = {
    position: {
      x: 0,
      y: 0,
      z: 4.5,
    },
    light: {
      fresnelMultiplier: uniform(0.8),
    },
  }

  constructor({
    scene,
    debugFolder,
    resources,
  }: {
    scene: THREE.Object3D
    debugFolder?: FolderApi
    resources: {
      [key: string]: LoadResult
    }
  }) {
    this.experience = Experience.getInstance()

    this.scene = scene
    this.resources = resources
    this.debugFolder = debugFolder

    this.setup()
    this.setMaterial()

    this.setActions()

    this.setEvents()
    this.setDebug()
  }

  setEvents() {
    this.experience.on(
      EVENTS.Guestbook.BeforeEnter,
      this.beforeEnterAnimation.bind(this),
    )

    this.experience.on(
      EVENTS.Guestbook.Entering,
      this.enteringAnimation.bind(this),
    )

    // this.experience.on(
    //   EVENTS.Guestbook.Entered,
    //   this.enteredAnimation.bind(this),
    // )
  }

  setup() {
    const manResource = this.resources["man"]
    assert(manResource, "Man resource not found")
    assert(manResource.scene, "Man resource scene not found")

    this.model = SkeletonUtils.clone(manResource.scene as THREE.Group)
    assert(this.model, "Model not found")

    this.model.rotation.y = Math.PI

    this.model.position.set(
      this.settings.position.x,
      this.settings.position.y,
      this.settings.position.z,
    )

    const animations = manResource.animations || []
    this.animatedModel = new AnimatedModel({
      source: this.model,
      animations,
      container: this.scene as THREE.Scene,
      debugFolder: this.debugFolder!,
    })

    const envMapResource = this.resources["guestbook-scene-map"]
    assert(envMapResource, "guestbook-scene-map resource not found")
    assert(
      envMapResource instanceof THREE.Texture,
      `guestbook-scene-map is not a valid texture. Got: ${typeof envMapResource}, value: ${envMapResource}`,
    )
    this.envMap = envMapResource

    this.perlin = this.experience.globalResources.perlin as THREE.Texture
    this.perlin.wrapS = THREE.RepeatWrapping
    this.perlin.wrapT = THREE.RepeatWrapping

    this.fbm = this.experience.globalResources.fbm as THREE.Texture
    this.fbm.wrapS = THREE.RepeatWrapping
    this.fbm.wrapT = THREE.RepeatWrapping

    // Get the SkinnedMesh directly to ensure same reference
    let skinnedMesh: THREE.SkinnedMesh | null = null
    this.model.traverse((child) => {
      if (child instanceof THREE.SkinnedMesh && !skinnedMesh) {
        skinnedMesh = child
      }
    })
    assert(skinnedMesh, "SkinnedMesh not found in model")

    this.outerParticles = new OuterParticles({
      scene: this.scene,
      man: skinnedMesh,
      debugFolder: this.debugFolder,
      fbm: this.fbm,
      perlin: this.perlin,
    })
  }

  beforeEnterAnimation() {
    this.animationCompleted = false
    this.setActions()
  }

  enteringAnimation(event: unknown) {
    if (this.animationCompleted) {
      return
    }

    const { progress } = event as { progress: number }

    const newTime = scaleValue(progress, [0, 1], [0, this.undiDuration - 0.001])

    const action = this.animatedModel.findAction("undi")

    if (!action) {
      return
    }

    this.animatedModel.mixer?.setTime(newTime)
    this.animatedModel.mixer?.update(0)
    action.timeScale = 0
    action.setEffectiveWeight(1)

    // const newTime = scaleValue(progress, [0, 1], [0, duration - 0.1])
    action.time = newTime
    action.timeScale = 0
    action.enabled = true

    if (progress === 1) {
      this.animationCompleted = true
    }
  }

  setActions() {
    const undiAction = this.animatedModel.findAction("undi")
    if (undiAction) {
      this.undiDuration = undiAction.getClip().duration

      undiAction.clampWhenFinished = true
      undiAction.loop = THREE.LoopOnce
      undiAction.reset()
      undiAction.setEffectiveTimeScale(1)
      undiAction.setEffectiveWeight(1)
      undiAction.play()
    }
  }

  setMaterial() {
    if (!this.model) {
      return
    }

    if (!this.envMap) {
      console.error("envMap is not defined, cannot create material")
      return
    }

    if (!(this.envMap instanceof THREE.Texture)) {
      console.error("envMap is not a valid texture", this.envMap)
      return
    }

    const material = new THREE.MeshBasicNodeMaterial()
    material.side = THREE.FrontSide

    const viewDir = normalize(positionWorld.sub(cameraPosition))
    const inverseFresnel = max(0, dot(viewDir.mul(-1), normalWorld))
    const fresnel = inverseFresnel.oneMinus()

    const colorShader = Fn(() => {
      const baseColor = vec3(1)
      const light = vec3(0, 0.003, 0.005)

      const refFresnel = pow(fresnel, 3)
      const refractDirR = normalize(refract(viewDir, normalWorld, div(1, 1.07)))
      const refractDirG = normalize(
        refract(viewDir, normalWorld, div(1, add(1.07, mul(0.2, refFresnel)))),
      )
      const refractDirB = normalize(
        refract(viewDir, normalWorld, div(1, add(1.07, mul(0.6, refFresnel)))),
      )
      const refractColorR = texture(this.envMap, refractDirR).r
      const refractColorG = texture(this.envMap, refractDirG).g
      const refractColorB = texture(this.envMap, refractDirB).b
      const refractColor = vec3(refractColorR, refractColorG, refractColorB)

      light.addAssign(
        refractColor.mul(fresnel).mul(this.settings.light.fresnelMultiplier),
      )

      const frColor = baseColor.mul(light)

      const finalColor = frColor

      return finalColor
    })

    material.colorNode = colorShader()

    this.model.traverse((child) => {
      if (child instanceof THREE.SkinnedMesh) {
        child.material = material
      }
    })
  }

  setDebug() {
    if (!this.debugFolder) {
      return
    }

    const folder = this.debugFolder.addFolder({
      title: "👤 Man",
      expanded: true,
    })

    const animationProgress = {
      value: 0,
    }

    folder
      .addBinding(animationProgress, "value", {
        label: "Animation Progress",
        min: 0,
        max: 1,
        step: 0.01,
      })
      .on("change", (ev) => {
        this.enteringAnimation({ progress: ev.value as number })
      })

    const uniforms = {
      fresnelMultiplier: this.settings.light.fresnelMultiplier.value,
    }

    folder
      .addBinding(uniforms, "fresnelMultiplier", {
        min: 0,
        max: 2,
        step: 0.01,
      })
      .on("change", (ev) => {
        this.settings.light.fresnelMultiplier.value = ev.value
      })
  }

  update(params: { delta: number; elapsed: number }) {
    this.animatedModel?.update(params)
    this.outerParticles?.update()
  }
}
